import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { Modal } from '../../components/ui/Modal.js';
import { formatDate } from '../../utils/date.js';
import { fetchCalendarWeek, type CalendarWeekEvent } from './graphCalendar.js';

interface CalendarWeekModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Monday of the timesheet week (yyyy-mm-dd). */
  weekStartDate: string;
}

const SHOW_AS_LABELS: Record<string, string> = {
  free: 'Free',
  tentative: 'Tentative',
  busy: 'Busy',
  oof: 'Out of office',
  workingElsewhere: 'Working elsewhere',
};

const SHOW_AS_TONES: Record<string, 'green' | 'gray' | 'amber' | 'blue'> = {
  free: 'gray',
  tentative: 'amber',
  busy: 'blue',
  oof: 'green',
  workingElsewhere: 'gray',
};

/** "9:00 AM" from a Graph local wall-clock dateTime string. */
function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Duration in hours (2 dp max) between two local wall-clock dateTime strings. */
function durationHours(start: string, end: string): number {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.round((ms / 3_600_000) * 100) / 100;
}

function formatHours(hours: number): string {
  return `${hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(2)} hr${hours === 1 ? '' : 's'}`;
}

interface DayGroup {
  dateIso: string;
  events: CalendarWeekEvent[];
  totalHours: number;
}

/**
 * Pop-up showing the signed-in member's Outlook calendar events for the timesheet's
 * week, so they can review meetings while deciding which hours to log. Read-only; the
 * events come straight from Microsoft Graph in the browser.
 */
export function CalendarWeekModal({
  isOpen,
  onClose,
  weekStartDate,
}: CalendarWeekModalProps): JSX.Element {
  const eventsQuery = useQuery({
    queryKey: ['graph', 'calendar-week', weekStartDate],
    queryFn: async () => {
      const result = await fetchCalendarWeek(weekStartDate);
      if (!result.ok) throw new Error(result.message);
      return result;
    },
    enabled: isOpen,
    staleTime: 60_000,
    retry: false,
  });

  // Category opt-in filter: all unchecked by default = no filtering.
  const [selectedCategories, setSelectedCategories] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (isOpen) setSelectedCategories(new Set());
  }, [isOpen, weekStartDate]);

  const allEvents = eventsQuery.data?.events;
  const categories = eventsQuery.data?.categories ?? [];

  const filteredEvents = useMemo<CalendarWeekEvent[]>(() => {
    const events = allEvents ?? [];
    if (selectedCategories.size === 0) return events;
    return events.filter((event) => event.categories.some((c) => selectedCategories.has(c)));
  }, [allEvents, selectedCategories]);

  const days = useMemo<DayGroup[]>(() => {
    const byDay = new Map<string, CalendarWeekEvent[]>();
    for (const event of filteredEvents) {
      const dateIso = event.start.slice(0, 10);
      const list = byDay.get(dateIso) ?? [];
      list.push(event);
      byDay.set(dateIso, list);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateIso, events]) => ({
        dateIso,
        events,
        totalHours: events.reduce(
          (sum, e) => sum + (e.isAllDay ? 0 : durationHours(e.start, e.end)),
          0,
        ),
      }));
  }, [filteredEvents]);

  const weekTotal = days.reduce((sum, day) => sum + day.totalHours, 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`My calendar — week of ${formatDate(weekStartDate)}`}
      size="lg"
      fillHeight
    >
      <p className="mb-4 text-sm text-slate-500">
        Meetings from your Outlook calendar for this timesheet&apos;s week, to help you decide
        which hours to log. Nothing here is saved to the PSA.
      </p>
      {categories.length > 0 ? (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Filter by category
            </span>
            {selectedCategories.size > 0 ? (
              <button
                type="button"
                className="text-xs font-medium text-brand-700 hover:underline"
                onClick={() => setSelectedCategories(new Set())}
              >
                Clear ({selectedCategories.size})
              </button>
            ) : (
              <span className="text-xs text-slate-400">None checked = all events</span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {categories.map((category) => (
              <label
                key={category}
                className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={selectedCategories.has(category)}
                  onChange={(e) =>
                    setSelectedCategories((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(category);
                      else next.delete(category);
                      return next;
                    })
                  }
                />
                {category}
              </label>
            ))}
          </div>
        </div>
      ) : null}
      <DataState
        isLoading={eventsQuery.isLoading}
        isError={eventsQuery.isError}
        error={eventsQuery.error}
        onRetry={() => void eventsQuery.refetch()}
        isEmpty={!eventsQuery.isLoading && !eventsQuery.isError && (allEvents ?? []).length === 0}
        emptyTitle="No calendar events"
        emptyDescription="Your Outlook calendar has no events during this week."
      >
        <div className="space-y-5">
          {days.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              No events match the selected categories.
            </p>
          ) : null}
          {days.map((day) => (
            <div key={day.dateIso}>
              <div className="mb-2 flex items-baseline justify-between border-b border-slate-200 pb-1">
                <h3 className="text-sm font-semibold text-slate-700">{formatDate(day.dateIso)}</h3>
                <span className="text-xs text-slate-500">
                  {formatHours(Math.round(day.totalHours * 100) / 100)} in meetings
                </span>
              </div>
              <ul className="space-y-2">
                {day.events.map((event) => (
                  <li
                    key={event.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{event.subject}</p>
                      <p className="text-xs text-slate-500">
                        {event.isAllDay
                          ? 'All day'
                          : `${formatTime(event.start)} – ${formatTime(event.end)} · ${formatHours(
                              durationHours(event.start, event.end),
                            )}`}
                        {event.organizer ? ` · ${event.organizer}` : ''}
                        {event.location ? ` · ${event.location}` : ''}
                        {event.categories.length > 0 ? ` · ${event.categories.join(', ')}` : ''}
                      </p>
                    </div>
                    {event.showAs && SHOW_AS_LABELS[event.showAs] ? (
                      <Badge tone={SHOW_AS_TONES[event.showAs] ?? 'gray'}>
                        {SHOW_AS_LABELS[event.showAs] ?? event.showAs}
                      </Badge>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {days.length > 0 ? (
            <p className="text-right text-sm font-medium text-slate-600">
              Week total: {formatHours(Math.round(weekTotal * 100) / 100)} in meetings
            </p>
          ) : null}
        </div>
      </DataState>
    </Modal>
  );
}
